import minmaxUtils from "_src/engine/utility/minmax";
import Chunk from "_src/engine/chunk";
import output from "_src/engine/output";
import workerManager from "_src/engine/worker_manager.js";
import { messageType, resultStatus } from "_src/engine/ipc.js";
import {recalculateBounding} from "_src/engine/boundings";

import Wrapper from "_src/wrapper";

import loader from "_src/loader";
import appState from "_src/app_state";
import config from "_src/config";
import bufferFactory from "_src/engine/factories/pixel_buffer";
import {
  pixelBufferType,
  bufferType,
  textureWrapper,
} from "_src/engine/constants";
import database from "_database";
import workspace from "_src/workspace";
import loadingProgress from "_src/loading_progress";
import compat from "_src/engine/compat";
import {unCompressDxt} from "_src/utils";
import {colorType, objectType} from "_src/constants";
import projectTree from "_src/reformer/project_tree";
import reformer from "_src/reformer/reformer";

export default class FrameWrapper extends Wrapper {

  constructor(params) {

    super(params);

    const parentID = params.parentID ? params.parentID : "";
    this._visibility = false;
    this.model = params.model !== undefined ? params.model : null;

    this.textureList = [];

    // NOTE: this is just a reference info. It's not intended for use in
    // algorithms
    this.numPolygons = 0;
    this.numVertices = 0;

    this._chunksMap = {};

    this.cacheID = "";

    this._loadingCanceled = false;
    this.shouldBeLoaded = false;

    this._tid = workerManager.INVALID_ID;

    this._cacheUnloadedData = config.cacheUnloadedData;

    this.geometryPromise = null;

    this.texturePromise = null;
    this.texturesLoaded = false;

    this.chunksToBeRemoved = [];

    this._selfPropertyGetter = (name) => projectTree._getProperty(parentID).getValue("frames").find(prop => prop.getValue("id") === this.getID()).getValue(name);
  }

  getVisibility() {
    return this._visibility;
  }

  isKeyFrame() {
    return this._selfPropertyGetter("keyframe");
  }

  getRegError() {
    return this._selfPropertyGetter("error");
  }

  copyLocalMinmax(minmax) {
    minmaxUtils.copy(this.getLocalMinmax(), minmax);
  }

  copyWorldMinmax(minmax) {
    minmaxUtils.copy(this.getWorldMinmax(), minmax);
  }

  getWorldMinmax() {
    return this.model.minmaxWorld;
  }

  getLocalMinmax() {
    return this.model.minmaxLocal;
  }

  expandWorldMinMax(minmax) {
    minmaxUtils.expandMinmax(this.getWorldMinmax(), minmax);
  }

  expandLocalMinMax(minmax) {
    minmaxUtils.expandMinmax(this.getLocalMinmax(), minmax);
  }

  loadGeometry(property, objectID, level = 0) {

    this.loaded = false;
    this._loadingCanceled = false;

    if (this.geometryPromise) {
      throw "Trying to load frame twice.";
    }

    this.geometryPromise = this._loadGeometryLevel(property, level, objectID).then(() => {
      this.geometryPromise = null;
    });

    return this.geometryPromise;

  }

  async unloadData() {

    if (this._cacheUnloadedData && this.loaded) {
      await database.putFrameData(this);
    }

    workerManager.terminateByID(this._tid);

    this.model.cleanupChunks();

    this.loaded = false;
    this._loadingCanceled = true;

  }

  unloadTextures() {
    if (this.hasTextures()) {
      const textureLevel = Math.min(config.scansTextureLevel, this.textureList[0].length - 1);
      const path = reformer.getProjectDirectory() + this.textureList[0][textureLevel]["path"];
      const inSliceId = this.textureList[0][textureLevel]["index"];
      loader.abortByPathAndId(path, inSliceId);
      this.texturesLoaded = false;
      this.model.cleanupTextures();
    }
  }

  isEmpty() {
    return !this.model;
  }

  setVisibility(visible) {
    this._visibility = visible;
    this.model.setVisibility(visible);
  }

  addTextureInfo(property) {
    const levels = [];
    for (const level of property.getValue("levels")) {
      levels.push({
        path: level.getValue("file"),
        width: level.getValue("width"),
        height: level.getValue("height"),
        channels: level.getValue("channels"),
        index: level.getValue("index"),
      });
    }

    this.textureList.push(levels);

  }

  initChunks(geometries) {

    for (let property of geometries) {
      const chunkID = property.getValue("id");
      let chunk = this.model.chunks.find(chunk => chunk.getID() === chunkID);
      if (!chunk) {
        chunk = new Chunk(chunkID);
        chunk.needsReload = true;
        this.addChunk(chunk);
      }

    }

  }

  async _loadGeometryLevel(property, level, objectID) {
    const loadedFromCache = this._cacheUnloadedData && (await database.getFrameData(this));
    if (loadedFromCache) return;

    const geometries = property.getValue("geometries").filter(geometryProperty => geometryProperty.getValue("levels").length > level);
    this.initChunks(geometries);

    const chunks = this.model.chunks;
    if (chunks.length !== geometries.length) {
      return Promise.reject({ message : `Chunks number mismatch for objectID ${objectID}, frame ${this.getID()}`});
    }

    await this.loadChunks(level, objectID, property);
  }

  loadChunks(level, objectID, property) {

    const promises = [];
    const chunks = this.model.chunks;

    for (let i = 0; i < chunks.length; ++i) {
      if (!chunks[i].needsReload) {
        continue;
      }
      promises.push(this.loadChunk(i, level, objectID, property));
    }
    return Promise.all(promises).then(() => {
      if (this._loadingCanceled) {
        this._chunksMap = {};
        this.model.cleanupChunks();
      } else {
        this.loaded = true;
      }
    });

  }

  loadChunk(index, level, objectID, property) {

    const chunk = this.model.chunks[index];
    const geometryProperty = property.getValue("geometries").find(property => property.getValue("id") === chunk.getID());

    return this.loadChunkGeometry(chunk, geometryProperty, level, loader.GEOMETRY, objectID).then((geometry) => {
      if (!geometry) {
        // NOTE: we cancel loading for invalid geometries
        this._loadingCanceled = true;
      }
      chunk.needsReload = false;
      loadingProgress.updateObjectStatus(workspace.getObjectByID(objectID), level);
    });

  }

  loadChunkGeometry(chunk, property, level, loaderType, objectID) {
    const minmax = property.getValue("box");
    const levels = property.getValue("levels");
    const path = reformer.getProjectDirectory() + levels[level].getValue("file");
    const inSliceID = levels[level].getValue("index");

    const payload = {
      objectID: objectID,
      frameID: this.getID(),
      id: inSliceID,
    };

    return loader.load(path, loaderType, payload).then((geometry) => {

      if (!geometry) {
        return null;
      }

      this._checkTextureConsistency(geometry, objectID);

      if (appState.getState(appState.GS_NORMALS) && !geometry.hasLayoutItem("a_normal")) {
        const count = geometry.getVerticesCount();
        geometry.initEmptyBuffer("a_normal", count, bufferType.F32_3);
      }

      if (appState.getStateValue("distanceMapMode") && !geometry.hasLayoutItem("a_distance") && this.model.shader.getDirectiveValue("COLOR_TYPE") === colorType.DISTANCE_MAP) {
        const count = geometry.getVerticesCount();
        geometry.initEmptyBuffer("a_distance", count, bufferType.F32_1);
      }

      const rm = appState.getStateValue("renderMode");
      const needsVertexID = rm === appState.RM_WIREFRAME;

      this.model.setChunkGeometry(chunk, geometry);
      chunk.updateSelectionMask();
      chunk.updateVertexID(needsVertexID);
      geometry.setBbox(minmax);
      this.model.updateChunkBBox(chunk);

      return geometry;

    }).catch((errorCode) => {
      if (!loader.isDecimationError(errorCode)) {
        throw errorCode;
      }

      return null;

    });
  }

  _checkTextureConsistency(geometry, objectID) {
    const parentObject = workspace.getObjectByID(objectID);
    if (parentObject.type === objectType.FUSION && this.textureList.length > 0 && !geometry.hasLayoutItem("a_uv")) {
      this.textureList.length = 0;
      parentObject.updateTextureUsage();
    }
  }

  loadTexture() {
    if (this.textureList.length > 0) {
      const textureLevel = Math.min(config.scansTextureLevel, this.textureList[0].length - 1);
      return this._loadTexture(0, textureLevel).then((loaded) => {
        if (loaded) {
          this.texturesLoaded = true;
          this.model.properties.enableLinearColorSpace = this.textureList.length === 0;
        }
      });
    }
    return Promise.resolve();
  }

  async _loadTexture(slot, level) {
    const path = reformer.getProjectDirectory() + this.textureList[slot][level]["path"];
    const inSliceId = this.textureList[slot][level]["index"];
    let compression = null;

    if (this.texturePromise) {
      await this.texturePromise;
    }

    this.texturePromise = loader.load(path, loader.IMAGE, {id: inSliceId}).then((image) => {

      if (!image) {
        output.error(output.DEVELOPERS_ERROR, "Can't load image on path: " + path);
        return;
      }

      let buffer = image.getBuffer();
      if (compression !== null && !compat.textureCompressionEnabled) {
        buffer = unCompressDxt(buffer, image.width, image.height, compression);
        compression = null;
      }
      const textureFormat = compression === null ? pixelBufferType.T_RGBA : pixelBufferType.T_COMPRESSED;

      // NOTE: we create a new texture here instead of resize of the older one
      const texture = bufferFactory.create(textureFormat,
        {
          width : image.width,
          height : image.height,
          immutable: true,
          format : compression,
        }
      );

      if (buffer) {
        texture.initMipMapLevelBuffer(0, buffer);
      } else {
        texture.initMipMapLevelImage(0, image);
      }

      this.model.setTexture(texture, slot);

      if (this.model.shader.getDirectiveValue("COLOR_TYPE") === colorType.TEXTURE_UV) {
        const chunks = this.model.chunks;
        for (let j = 0; j < chunks.length; ++j) {
          chunks[j].markAttributeDirty("a_uv");
        }
      }
      return true;
    }).catch((error) => {
      if (!loader.isLegalError(error)) {
        output.error(output.DEVELOPERS_ERROR, "Can't load image. " + error);
      }
      return false;
    }).finally(() => {
      this.texturePromise = null;
    });

    return this.texturePromise;
  }

  abortLoading(object) {

    for (const chunk of this.model.chunks) {
      const path = workspace.getChunkPath(object, this.id, chunk.id);
      if (path) loader.abortByPath(path);
    }
  }

  checkIsGeometryLoading() {
    return Boolean(this.geometryPromise);
  }

  getGeometryLoadingPromise() {
    return this.geometryPromise;
  }

  updateBoundingsByTransform() {
    const worldMinmax = minmaxUtils.recomputedFromTransform([], this.getLocalMinmax(), this.model.modelMatrix);
    this.copyWorldMinmax(worldMinmax);
  }

  calculateBoundings() {

    const minmax = this.getWorldMinmax();

    if (this.model.chunks.length === 0) {
      minmaxUtils.reset(minmax);
      return Promise.resolve(minmax);
    }

    if (config.useWorkers) {
      return this._calculateBoundingsWorker();
    } else {
      return this._calculateBoundingsMain();
    }

  }

  hasTextures() {
    return this.textureList.length > 0;
  }

  _calculateBoundingsMain() {

    const minmax = this.getWorldMinmax();
    const chunks = this.model.chunks;

    minmaxUtils.reset(minmax);

    const toWorld = this.model.modelMatrix;

    const promises = [];

    for (let i = 0; i < chunks.length; ++i) {

      const geometry = chunks[i].geometry;

      const positions = geometry.getItemBuffer("a_position");
      // NOTE: it's a hack. Server side corrupts geometry boxes
      minmaxUtils.reset(geometry.bbox);

      promises.push(recalculateBounding(positions, toWorld, minmax, geometry.bbox));
    }

    return Promise.all(promises).then(() => {
      return minmax;
    });

  }

  _calculateBoundingsWorker() {

    const message = {};
    const transfer = [];

    message["indices"] = {};
    message["positions"] = {};

    for (const chunk of this.model.chunks) {

      const geometry = chunk.geometry;
      const chunkID = chunk.getID();

      const positions = geometry.getItemBuffer("a_position");
      message["positions"][chunkID] = positions;
      const positionsItem = geometry.getLayoutItem("a_position");
      positionsItem.setIsDetached(true);
      transfer.push(positions.buffer);

    }

    message.type    = messageType.RECALCULATE_BOUNDINGS;
    message.toWorld = this.model.modelMatrix;

    this._tid = workerManager.generateUniqueID();

    return workerManager.sendMessage(message, transfer, this._tid).then(({ data }) => {

      minmaxUtils.copy(this.getWorldMinmax(), data.minmax);

      for (let id in data["positions"]) {

        const chunk = this.getChunkByID(id);
        if (chunk === null) {
          output.error(output.DEVELOPERS_ERROR, "Can't calculate boundings for incomplete frames.");
          continue;
        }
        const geometry = chunk.geometry;

        minmaxUtils.copy(geometry.bbox, data.boxes[id]);

        const positionsItem = geometry.getLayoutItem("a_position");
        positionsItem.setBuffer(data["positions"][id].buffer);

      }

      return this.getWorldMinmax();

    }).catch((error) => {
      if (error.code != resultStatus.TERMINATED) {
        output.error(output.DEVELOPERS_ERROR, "Can't recalculate boundings. " + error.message);
      }
      return this.getWorldMinmax();
    });

  }

  updateNumPolygons() {

    this.numPolygons = 0;

    const chunks = this.model.chunks;
    for (let i = 0; i < chunks.length; ++i) {
      this.numPolygons += chunks[i].geometry.getTrianglesCount();
    }

  }

  updateNumVertices() {

    this.numVertices = 0;

    const chunks = this.model.chunks;
    for (let i = 0; i < chunks.length; ++i) {
      this.numVertices += chunks[i].geometry.getVerticesCount();
    }

  }

  dropTextures() {

    this.textureList.length = 0;

    const shader = this.model.shader;
    shader.setDirective("COLOR_TYPE", colorType.UNIFORM);

  }

  cleanup() {

    this.model.cleanup();
    this.model = null;
    this._chunksMap = {};

    this.textureList = [];
    this.chunksToBeRemoved.length = 0;

  }

  getChunkByID(id) {
    return this._chunksMap[id] || null;
  }

  addChunk(chunk) {
    this.model.addChunk(chunk);
    this._chunksMap[chunk.getID()] = chunk;
    chunk.color = new Float32Array([Math.random(), Math.random(), Math.random()]);
  }

  _deleteChunk(chunk) {

    const id = chunk.getID();

    delete this._chunksMap[id];

    const index = this.model.chunks.indexOf(chunk);

    this.model.chunks.splice(index, 1);

    chunk.cleanup();

  }

  deleteChunk(chunk, keepSaveInfo = true) {

    this._deleteChunk(chunk);

    if (keepSaveInfo) {
      this.chunksToBeRemoved.push(chunk.getID());
    }

  }

  async initByExternalData(objectData, progressCallback = null) {
    const geometry = objectData.geometry;
    const chunk = new Chunk();
    this.addChunk(chunk);
    this.model.setChunkGeometry(chunk, geometry);
    this.copyLocalMinmax(geometry.bbox);
    this.copyWorldMinmax(geometry.bbox);
    if (config.viewOnlyMode) {
      for (const item of Object.values(geometry.getLayout())) {
        item.scheduleUnload();
      }
    }

    const getProgressHandler = (() => {
      const partials = [];

      return (() => {
        if (progressCallback === null) return null;

        const partialNumber = partials.length;
        partials.push(0);

        return function(partialProgress) {
          partials[partialNumber] = partialProgress;
          const sum = partials.reduce((partialSum, a) => partialSum + a, 0);
          progressCallback(sum / partials.length);
        }
      });
    })();

    const material = objectData.material;
    const texturingPromises = [];
    const textureList = [material.baseTexture, material.metallicRoughnessTexture];
    for (let i=0; i<textureList.length; i++) {
      const texturePath = textureList[i];

      let loadingPromise;
      if (texturePath) {
        const loadPayload = {fileProgress: getProgressHandler()};
        loadingPromise = loader.load(texturePath, loader.IMAGE, loadPayload).then(image => {
          if (i === 0) this.textureList.push({}); //dummy textureInfo to mark that frame has texture
          return image;
        }).catch(e => {
          output.warn("Can't load model texture: " + e);
        });
      } else {
        loadingPromise = Promise.resolve(null);
      }

      const texturingPromise = loadingPromise.then(image => {
        const width = image ? image.width : 1;
        const height = image ? image.height : 1;
        const texture = bufferFactory.create(pixelBufferType.T_RGBA,
            {
              width: width,
              height: height,
              immutable: true,
              flipY: material.flipTexture,
              wrapper: textureWrapper.REPEAT,
            }
        );
        if (image) {
          const buffer = image.getBuffer();
          if (buffer) {
            texture.initMipMapLevelBuffer(0, buffer);
          } else {
            texture.initMipMapLevelImage(0, image);
          }
        }

        this.model.setTexture(texture, i);
      });

      texturingPromises.push(texturingPromise);
    }
    await Promise.allSettled(texturingPromises);
    this.texturesLoaded = true;

    if (progressCallback) progressCallback(1);
    this.loaded = true;
  }
}
